// 시작 실패 안내 화면. GPU를 쓸 수 없거나 장치 연결이 반복해서 끊기면 플레이 화면 대신 원인·설정·다시 시도를 보여 준다.
// 오류 문구는 모두 textContent로 넣는다(원시 예외 문자열을 HTML로 해석하지 않는다). 기술 정보는 접힌 details에만 둔다.
import { RECOVER_PARAM, RECOVER_STORE_KEY, StartupError, type StartupFailureCode } from '../render/gpuProbe';

export interface StartupMessage {
  title: string;
  cause: string;
  tips: string[];
}

export function startupMessage(code: StartupFailureCode): StartupMessage {
  if (code === 'no-gpu') {
    return {
      title: '게임 화면을 열 수 없어요',
      cause: '지금 브라우저에서 3D 그래픽 기능을 쓸 수 없는 상태예요. 브라우저의 하드웨어 가속이 꺼져 있거나, 그래픽 드라이버 문제로 브라우저가 그래픽 기능을 막았을 때 주로 생겨요.',
      tips: [
        'Chrome·Edge: 설정 → 시스템에서 “가능한 경우 그래픽 가속 사용”을 켜고 브라우저를 완전히 닫았다가 다시 여세요.',
        '그래픽 드라이버를 최신 버전으로 업데이트해 보세요.',
        '최신 Chrome이나 Edge에서 다시 열어 보세요.',
      ],
    };
  }
  if (code === 'gpu-lost') {
    return {
      title: '그래픽 연결이 계속 끊겨요',
      cause: '게임을 그리는 중 그래픽 장치와의 연결이 여러 번 끊겼어요. 그래픽 드라이버가 다시 시작됐거나 다른 프로그램이 그래픽 자원을 많이 쓰고 있을 수 있어요.',
      tips: [
        '다른 무거운 탭이나 프로그램을 닫고 다시 시도해 보세요.',
        'Chrome·Edge: 설정 → 시스템에서 “가능한 경우 그래픽 가속 사용”이 켜져 있는지 확인하세요.',
        '그래픽 드라이버를 최신 버전으로 업데이트해 보세요.',
      ],
    };
  }
  return {
    title: '게임을 시작하지 못했어요',
    cause: '게임을 준비하는 중 문제가 생겼어요. 잠시 뒤 다시 시도해 주세요.',
    tips: ['네트워크 연결을 확인하고 새로고침해 보세요.'],
  };
}

export interface RetryLinks {
  /** 같은 설정으로 다시 열기(자동 재시작 횟수는 지운다) */
  reload: string;
  /** 호환 모드(renderer=webgl) 또는 기본 모드(renderer=auto: 설정·세션 기록보다 우선)로 다시 열기 */
  alternate: string;
  /** 지금이 호환 모드인지(버튼 문구를 고른다) */
  compatActive: boolean;
}

/** compatActive: 지금 호환 모드로 돌고 있는지(URL·세션·설정을 합친 값). 생략하면 URL만 본다. */
export function retryLinks(href: string, compatActive?: boolean): RetryLinks {
  const base = new URL(href);
  base.searchParams.delete(RECOVER_PARAM);
  const compat = compatActive ?? base.searchParams.get('renderer') === 'webgl';
  const alt = new URL(base.toString());
  alt.searchParams.set('renderer', compat ? 'auto' : 'webgl');
  return { reload: base.toString(), alternate: alt.toString(), compatActive: compat };
}

export function toStartupError(err: unknown): StartupError {
  if (err instanceof StartupError) return err;
  const text = err instanceof Error ? err.name + ': ' + err.message : String(err);
  return new StartupError('boot', [text], err);
}

const OVERLAY_ID = 'dh-startup-error';

/**
 * 안내 화면을 띄운다(이미 떠 있으면 바꾼다). sessionKey는 '기본 모드' 복귀 때 지울 세션 기록 키,
 * compatActive는 지금 호환 모드인지. 사용자가 누르는 재시도는 자동 재시작 횟수(URL·세션)를 모두 지운다.
 */
export function showStartupError(doc: Document, err: unknown, opts: { sessionKey?: string; compatActive?: boolean } = {}): HTMLElement {
  const e = toStartupError(err);
  const m = startupMessage(e.code);
  const links = retryLinks(doc.location.href, opts.compatActive);
  doc.getElementById(OVERLAY_ID)?.remove();
  const make = <K extends keyof HTMLElementTagNameMap>(tag: K, css: string, text?: string): HTMLElementTagNameMap[K] => {
    const n = doc.createElement(tag);
    n.style.cssText = css;
    if (text !== undefined) n.textContent = text;
    return n;
  };
  const root = make('div', 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box;background:#0d1220;color:#eef1f7;font-family:system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif;pointer-events:auto;overflow:auto;user-select:text;-webkit-user-select:text;touch-action:auto');
  root.id = OVERLAY_ID;
  root.setAttribute('role', 'alert');
  root.dataset.code = e.code;
  const card = make('div', 'max-width:560px;width:100%;background:#18213a;border:1px solid #2c3a5e;border-radius:14px;padding:24px 26px;box-shadow:0 10px 40px #0008;line-height:1.55');
  card.append(make('h1', 'margin:0 0 10px;font-size:22px;color:#ffd76a', m.title));
  card.append(make('p', 'margin:0 0 14px;font-size:15px', m.cause));
  const ul = make('ul', 'margin:0 0 18px;padding-left:20px;font-size:14px;color:#c9d2e6');
  for (const t of m.tips) ul.append(make('li', 'margin:4px 0', t));
  card.append(ul);
  const row = make('div', 'display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px');
  const btn = (label: string, primary: boolean, go: () => void): HTMLButtonElement => {
    const b = make('button', 'font:inherit;font-size:15px;padding:10px 16px;border-radius:9px;cursor:pointer;border:1px solid ' + (primary ? '#ffd76a;background:#ffd76a;color:#1a2238;font-weight:700' : '#4a5a85;background:transparent;color:#eef1f7'), label);
    b.type = 'button';
    b.addEventListener('click', go);
    return b;
  };
  const clearSession = (keys: string[]): void => {
    try { const s = doc.defaultView?.sessionStorage; for (const k of keys) s?.removeItem(k); } catch { /* 저장소 거부: URL만으로 다시 연다 */ }
  };
  const reload = btn('새로고침', true, () => {
    clearSession([RECOVER_STORE_KEY]);
    doc.location.assign(links.reload);
  });
  row.append(reload);
  row.append(btn(links.compatActive ? '기본 모드로 다시 시도' : '호환 모드로 다시 시도', false, () => {
    clearSession(links.compatActive && opts.sessionKey ? [RECOVER_STORE_KEY, opts.sessionKey] : [RECOVER_STORE_KEY]);
    doc.location.assign(links.alternate);
  }));
  card.append(row);
  const details = make('details', 'font-size:12px;color:#9aa6c2');
  details.append(make('summary', 'cursor:pointer', '기술 정보'));
  const ua = doc.defaultView?.navigator?.userAgent ?? '';
  details.append(make('pre', 'white-space:pre-wrap;word-break:break-all;margin:8px 0 0;font-size:12px', ['code: ' + e.code, ...e.diagnostics, ua].filter(Boolean).join('\n')));
  card.append(details);
  root.append(card);
  doc.body.append(root);
  try { reload.focus(); } catch { /* 무시 */ }
  return root;
}
